import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import { ErpConnection } from './ErpConnections/types';
import { ERP_TYPES } from './ErpConnections/constants';
import SalesforceNavigation from './ErpConnections/SalesforceNavigation';
import { ErpBinding } from './ModelDataFields/Input/reusables/ErpBindingButton';
import TailwindModal from './Tailwind/TailwindModal';
import { cn } from '../lib/utils';

const BINDING_NAV_STATE_KEY = 'erp_binding_nav_state';

export interface BindingNavigationState {
    connectionId: number;
    objectType: string;
    filters: { field: string; operator: string; value: any }[];
    selectedFields: string[];
    pageSize: number;
}

export const saveBindingNavigationState = (state: BindingNavigationState) => {
    try {
        localStorage.setItem(BINDING_NAV_STATE_KEY, JSON.stringify(state));
    } catch (error) {
        console.error('Failed to save binding navigation state:', error);
    }
};

export const loadBindingNavigationState = (): BindingNavigationState | null => {
    try {
        const stored = localStorage.getItem(BINDING_NAV_STATE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.error('Failed to load binding navigation state:', error);
        return null;
    }
};

interface FieldBindingModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    fieldName: string;
    field: any;
    modelDataId: number;
    modelDataName: string;
    scenarioId: number;
    bindingKey?: string;
    existingBinding?: ErpBinding | null;
    onBindingCreated?: () => void;
    currentValue?: any;
    onSyncApply?: (erpValue: any) => void;
}

export default function FieldBindingModal({
    open,
    setOpen,
    fieldName,
    field,
    modelDataId,
    modelDataName,
    scenarioId,
    bindingKey,
    existingBinding,
    onBindingCreated,
    currentValue,
    onSyncApply,
}: FieldBindingModalProps) {
    const [connections, setConnections] = useState<ErpConnection[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedConnectionId, setSelectedConnectionId] = useState<number | ''>('');

    useEffect(() => {
        if (open) {
            loadConnections();
        }
    }, [open]);

    useEffect(() => {
        if (connections.length > 0) {
            if (existingBinding) {
                setSelectedConnectionId(existingBinding.connection_id);
            } else {
                const savedState = loadBindingNavigationState();
                if (savedState && connections.some(c => c.id === savedState.connectionId)) {
                    setSelectedConnectionId(savedState.connectionId);
                }
            }
        }
    }, [existingBinding, connections]);

    const loadConnections = async () => {
        setLoading(true);
        try {
            const response = await apiService.getErpConnections();
            if (response) {
                const data = await response.json();
                setConnections(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Failed to load ERP connections:', error);
        }
        setLoading(false);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedConnectionId('');
    };

    const selectedConnection = connections.find(c => c.id === selectedConnectionId);

    return (
        <TailwindModal
            isOpen={open}
            onClose={handleClose}
            title="ERP Binding"
            size="5xl"
        >
            <div className="flex flex-col gap-4 max-h-[80vh] overflow-y-auto -mx-6 -my-4 px-6 py-4">
                {/* Context info */}
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-sm text-slate-600">
                    <p><span className="font-semibold text-slate-800">Object:</span> {modelDataName}</p>
                    <p className="mt-0.5">
                        <span className="font-semibold text-slate-800">Field:</span> {fieldName}
                        <span className="ml-1 text-xs text-slate-400">({field.type})</span>
                        {bindingKey && <span className="ml-1 font-semibold text-slate-700">— {bindingKey}</span>}
                    </p>
                </div>

                <hr className="border-slate-200" />

                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
                    </div>
                ) : connections.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <span className="material-symbols-outlined text-[40px] text-slate-300">link_off</span>
                        <p className="text-sm text-slate-500">No ERP connections available.</p>
                        <p className="text-xs text-slate-400">Please create a connection in Organization settings first.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {/* Connection selector */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                ERP Connection
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {connections.map((connection) => {
                                    const erpConfig = ERP_TYPES[connection.type];
                                    const isSelected = selectedConnectionId === connection.id;
                                    return (
                                        <button
                                            key={connection.id}
                                            onClick={() => setSelectedConnectionId(connection.id)}
                                            className={cn(
                                                'flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm transition-colors',
                                                isSelected
                                                    ? 'border-primary bg-primary/5 text-primary'
                                                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                                            )}
                                        >
                                            {erpConfig?.logo && (
                                                <img
                                                    src={erpConfig.logo}
                                                    alt={erpConfig.label}
                                                    className="h-5 w-auto max-w-[50px] object-contain"
                                                />
                                            )}
                                            <div className="text-left">
                                                <div className="font-medium leading-tight">{connection.name}</div>
                                                <div className="text-xs text-slate-400 leading-tight">{erpConfig?.label || connection.type}</div>
                                            </div>
                                            {isSelected && (
                                                <span className="material-symbols-outlined text-[18px] text-primary ml-1">check_circle</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedConnection && (
                            selectedConnection.type === 'salesforce' ? (
                                <SalesforceNavigation
                                    connectionId={selectedConnection.id}
                                    appFieldType={field.type}
                                    bindingKey={bindingKey}
                                    valueId={field.id}
                                    scenarioId={scenarioId}
                                    existingBinding={existingBinding}
                                    currentValue={currentValue}
                                    onSyncApply={onSyncApply ? (erpValue) => {
                                        onSyncApply(erpValue);
                                        onBindingCreated?.();
                                        handleClose();
                                    } : undefined}
                                    onSkipApply={() => {
                                        handleClose();
                                    }}
                                    onBindingCreated={() => {
                                        onBindingCreated?.();
                                        if (!onSyncApply) {
                                            setTimeout(() => {
                                                handleClose();
                                            }, 1500);
                                        }
                                    }}
                                    onBindingDeleted={() => {
                                        onBindingCreated?.();
                                        setTimeout(() => {
                                            handleClose();
                                        }, 1500);
                                    }}
                                    onFieldSelect={(selectedField, objectType) => {
                                        console.log('Selected field:', selectedField, 'from object:', objectType);
                                    }}
                                />
                            ) : (
                                <div className="text-center text-slate-400 py-6 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                                    Navigation for {selectedConnection.type} connections will be available here.
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
        </TailwindModal>
    );
}
